import { useRef, useState } from 'react';
import { singletonHook } from 'react-singleton-hook';
import { logger } from '../../util/logger';
import { isEnglish, hashCode } from '../../util/helpers';
import { useTranslationStore } from './useTranslationStore';
const { onTranslationUpdate, getCachedTranslation, queueTranslation, getPhraseHash
// eslint-disable-next-line react-hooks/rules-of-hooks
 } = useTranslationStore();
/**
 * This method will be modified by the singleton hook once it
 * has been mounted to allow for dynamically loading translations
 * On initial render it can still lookup a translation in cache
 * If none is found it returns the original phrase. Missing translations
 * will be fetched on the next render
 * @param phrase
 * @returns
 */
let globalTranslate = (phrase) => {
    logger.log('make sure to call useTranslate() in the component that uses $t otherwise translations can only be fetched from cache');
    return getCachedTranslation(phrase) || phrase;
};
const useTranslationImpl = () => {
    const translations = useRef({});
    const [, setRenderTrigger] = useState('');
    const forceRerender = () => {
        setRenderTrigger(hashCode(JSON.stringify(translations.current)));
    };
    function addPhraseToTranslationsRef(phrase) {
        const hash = getPhraseHash(phrase);
        translations.current = {
            ...translations.current,
            ...{
                [hash]: phrase
            }
        };
    }
    function addTranslatedPhraseToTranslationsRef(hash, translatedPhrase) {
        translations.current = {
            ...translations.current,
            ...{
                [hash]: translatedPhrase
            }
        };
    }
    // subscribe to translation updates
    onTranslationUpdate((translationRecords) => {
        // add new translations to storage
        translations.current = {
            ...translations.current,
            ...translationRecords
        };
        forceRerender();
    });
    // modify globalTranslate to dynamically load translations
    globalTranslate = (phrase) => {
        const hash = getPhraseHash(phrase);
        const translation = translations.current[hash];
        if (typeof translation === 'undefined') {
            const cachedTranslation = getCachedTranslation(phrase);
            if (cachedTranslation) {
                logger.log('found translation for', phrase, 'in cache');
                addTranslatedPhraseToTranslationsRef(hash, cachedTranslation);
            }
            else {
                logger.log('fetch translation for', phrase);
                queueTranslation(phrase);
                addPhraseToTranslationsRef(phrase);
            }
        }
        return translations.current[hash];
    };
    return translations.current;
};
export const $t = (phrase) => {
    if (isEnglish()) {
        return phrase;
    }
    return globalTranslate(phrase);
};
export const useTranslation = singletonHook({}, useTranslationImpl);
